import { useState, useMemo } from 'react';
import { Table, Button, Form, Pagination } from 'react-bootstrap';
import { FaTimes, FaCheck } from 'react-icons/fa';
import Loader from '../../components/Loader';
import Message from '../../components/Message';
import { useGetOrdersQuery } from '../../slices/ordersApiSlice';
import { Link } from 'react-router-dom';
import SummaryHeader from '../../components/SummaryHeader.jsx';

const OrderListScreen = () => {
  const { data: orders, isLoading, error } = useGetOrdersQuery();

  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('deliveredAll');
  const [sortOrder, setSortOrder] = useState('asc');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // GROUP + ANALYZE ORDER DATA PER USER
  const users = useMemo(() => {
    if (!orders) return [];

    const map = {};

    orders.forEach(order => {
        if (!order.user?._id) return;

        const uid = order.user._id;

        if (!map[uid]) {
            map[uid] = {
            userId: uid,
            name: order.user.name,
            totalPaid: 0,
            totalUnpaid: 0,
            totalBooks: 0,
            totalOrders: 0,
            deliveredAll: true,
            orders: []
            };
        }

        const user = map[uid];

        // Count orders
        user.totalOrders += 1;
        
        const books = Array.isArray(order.orderItems)
            ? order.orderItems.length
            : 0;

        user.totalBooks += books;

        // Paid / unpaid (ensure numeric)
        const itemsPrice = Number(order.itemsPrice || 0);

        if (order.isPaid) user.totalPaid += itemsPrice;
        else user.totalUnpaid += itemsPrice;

        // Delivered check
        if (!order.isDelivered) user.deliveredAll = false;

        user.orders.push(order);
    });

    return Object.values(map);
  }, [orders]);

  // SUMMARY TOTALS FOR HEADER
  const headerTotals = useMemo(() => ({
    totalPaid: users.reduce((s, u) => s + Number(u.totalPaid || 0), 0),
    totalUnpaid: users.reduce((s, u) => s + Number(u.totalUnpaid || 0), 0),
    totalBooks: users.reduce((s, u) => s + Number(u.totalBooks || 0), 0),
    totalOrders: users.reduce((s, u) => s + Number(u.totalOrders || 0), 0),
    deliveredCount: users.filter(u => u.deliveredAll).length,
    userCount: users.length
  }), [users]);

  // SEARCH + SORT
  const filteredUsers = useMemo(() => {
    let list = [...users];

    if (search) {
      list = list.filter(u =>
        u.name.toLowerCase().includes(search.toLowerCase())
        // u.userId.toLowerCase().includes(search.toLowerCase())
      );
    }

    list.sort((a, b) => {
      let valA = a[sortField];
      let valB = b[sortField];

      if (typeof valA === 'string') valA = valA.toLowerCase();
      if (typeof valB === 'string') valB = valB.toLowerCase();

      if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
      if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
  }, [users, search, sortField, sortOrder]);

  // PAGINATED LIST
  const totalPages = Math.ceil(filteredUsers.length / pageSize);
  const paginatedUsers = filteredUsers.slice((page - 1) * pageSize, page * pageSize);
  console.log('paginatedUsers', paginatedUsers);

  const handleSort = (field) => {
    if (sortField === field)
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Sort icon
  const SortIcon = ({ field }) =>
    sortField === field ? (sortOrder === 'asc' ? ' ▲' : ' ▼') : '';

  return (
    <>
      <h1 className="display-6 display-md-5 text-center">Alle Bestellingen</h1>

      <SummaryHeader
        totalPaid={headerTotals.totalPaid}
        totalUnpaid={headerTotals.totalUnpaid}
        totalBooks={headerTotals.totalBooks}
        totalOrders={headerTotals.totalOrders}
        deliveredCount={headerTotals.deliveredCount}
        userCount={headerTotals.userCount}
      />

      {isLoading ? <Loader /> : error ? (
        <Message variant="danger">{error?.data?.message}</Message>
      ) : (
        <>
            {/* SEARCH */}
            <div
                className="my-3 mx-auto"
                style={{
                    maxWidth: "600px",
                    width: "100%",
                }}
            >
            <Form.Control
                placeholder="Zoek op naam…"
                className="mb-3"
                value={search}
                onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
                }}
                style={{
                borderRadius: "12px",
                padding: "12px 16px",
                border: "2px solid #03337cff",
                boxShadow: "0 0 10px rgba(3, 96, 86, 0.67)",
                fontSize: "16px",
                }}
            />
            </div>

            {/* TABLE */}
            <Table striped bordered hover responsive>
                <thead>
                <tr>
                    {/* <th onClick={() => handleSort('userId')} style={{ cursor: 'pointer' }}>
                    ID {<SortIcon field="userId" />}
                    </th> */}
                    <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                    Naam {<SortIcon field="name" />}
                    </th>
                    <th onClick={() => handleSort('totalPaid')} style={{ cursor: 'pointer' }}>
                    Totaal Betaald {<SortIcon field="totalPaid" />}
                    </th>
                    <th onClick={() => handleSort('totalUnpaid')} style={{ cursor: 'pointer' }}>
                    Totaal Te Betalen {<SortIcon field="totalUnpaid" />}
                    </th>
                    <th onClick={() => handleSort('totalBooks')} style={{ cursor: 'pointer' }}>
                    Boeken {<SortIcon field="totalBooks" />}
                    </th>
                    <th onClick={() => handleSort('totalOrders')} style={{ cursor: 'pointer' }}>
                    Bestellingen {<SortIcon field="totalOrders" />}
                    </th>
                    <th onClick={() => handleSort('deliveredAll')} style={{ cursor: 'pointer' }}>
                    Alles Geleverd? {<SortIcon field="deliveredAll" />}
                    </th>
                    <th>Detail</th>
                </tr>
                </thead>
                <tbody>
                {paginatedUsers.map(u => (
                    <tr key={u.userId} className={!u.deliveredAll ? "table-warning" : ""}>
                    {/* <td>{u.userId}</td> */}
                    <td>{u.name}</td>
                    <td>€ {Number(u.totalPaid).toFixed(2)}</td>
                    <td>€ {Number(u.totalUnpaid).toFixed(2)}</td>
                    <td>{u.totalBooks}</td>
                    <td>{u.totalOrders}</td>
                    <td>{u.deliveredAll ? <FaCheck color="green" /> : <FaTimes color="red" />}</td>
                    <td>
                        <div className="d-flex align-items-center justify-content-center gap-2">
                            <Button
                            as={Link}
                            to={`/users/${u.userId}`}
                            className="btn-sm mx-2"
                            >
                            Bekijk
                            </Button>
                            <div>|</div>
                            <Button
                            as={Link}
                            to={`/admin/users/${u.userId}/items`}
                            className="btn-sm mx-2"
                            style={{ color: 'white', backgroundColor: '#099302ff', borderColor: '#099302ff' }}
                            >
                            Unprocessed
                            </Button>
                        </div>
                    </td>
                    </tr>
                ))}
                </tbody>
            </Table>

            {/* PAGINATION */}
            <Pagination>
                {[...Array(totalPages).keys()].map(x => (
                <Pagination.Item
                    key={x + 1}
                    active={x + 1 === page}
                    onClick={() => setPage(x + 1)}
                >
                    {x + 1}
                </Pagination.Item>
                ))}
            </Pagination>
        </>
      )}
    </>
  );
};

export default OrderListScreen;